<template>
  <v-card elevation="0" class="contract-sctr-form">
    <v-container fluid class="px-4 py-2">
      <!-- Aviso informativo -->
      <v-alert
        type="warning"
        outlined
        dense
        icon="mdi-shield-alert"
        class="mb-4"
      >
        <strong>SCTR Activo:</strong> Este contrato incluye Seguro
        Complementario de Trabajo de Riesgo. Complete los datos de pensión y
        salud SCTR para el trabajador.
      </v-alert>

      <!-- Fechas SCTR -->
      <v-row dense class="mb-2">
        <v-col cols="12" sm="6">
          <s-date
            v-model="sctrBeginDate"
            label="Fecha Inicio SCTR *"
            dense
            outlined
          />
        </v-col>
        <v-col cols="12" sm="6">
          <s-date
            v-model="sctrEndDate"
            label="Fecha Fin SCTR"
            dense
            outlined
          />
        </v-col>
      </v-row>

      <!-- Sección: Pensión SCTR -->
      <v-card flat outlined class="mb-4 section-card section-pension">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="indigo"
            >mdi-shield-account-outline</v-icon
          >
          {{ editedPensionIndex > -1 ? "EDITAR" : "NUEVA" }} PENSIÓN SCTR
          <v-spacer />
          <v-btn
            v-if="editedPensionIndex > -1"
            icon
            small
            color="error"
            @click="cancelPension"
            title="Cancelar"
          >
            <v-icon small>mdi-close-circle-outline</v-icon>
          </v-btn>
          <v-btn icon small color="success" @click="addPension" title="Agregar pensión SCTR">
            <v-icon small>mdi-plus-circle-outline</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <v-col cols="12">
              <s-select-definition
                :def="1499"
                label="Régimen Pensionario SCTR"
                v-model="pensionForm.TypeSctrPension"
                ref="sctrPensionRef"
                :add="$fun.isAdmin()"
                dense
              />
            </v-col>
          </v-row>

          <!-- Lista de regímenes SCTR registrados -->
          <v-data-table
            :headers="pensionHeaders"
            :items="pensionList"
            no-data-text="Sin regímenes pensionarios SCTR registrados"
            class="mt-3 elevation-0 outlined-table"
            dense
            hide-default-footer
          >
            <template v-slot:[`item.TypeSctrPensionName`]="{ item }">
              <div class="d-flex align-center">
                <v-icon small class="mr-1" color="indigo">mdi-shield-account</v-icon>
                {{ item.TypeSctrPensionName || "—" }}
              </div>
            </template>

            <!-- Chip USAR / ACTIVO (Selección única) -->
            <template v-slot:[`item.selected`]="{ item }">
              <v-chip
                small
                style="cursor: pointer"
                :color="item.selected ? 'indigo darken-2' : 'grey lighten-2'"
                :text-color="item.selected ? 'white' : 'grey darken-2'"
                :disabled="item.SecStatus === 0"
                @click="selectPension(item)"
              >
                <v-icon x-small left>{{
                  item.selected ? "mdi-check-circle" : "mdi-radiobox-blank"
                }}</v-icon>
                {{ item.selected ? "ACTIVO" : "USAR" }}
              </v-chip>
            </template>

            <template v-slot:[`item.SecStatus`]="{ item }">
              <v-icon small :color="item.SecStatus === 1 ? 'success' : 'grey'">
                {{ item.SecStatus === 1 ? "mdi-circle" : "mdi-circle-outline" }}
              </v-icon>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                small
                class="mr-1"
                color="primary"
                @click="editPension(item)"
                title="Editar"
                >mdi-pencil-outline</v-icon
              >
              <v-icon
                small
                :color="item.SecStatus === 1 ? 'error' : 'success'"
                @click="togglePension(item)"
                :title="item.SecStatus === 1 ? 'Desactivar' : 'Activar'"
              >
                {{
                  item.SecStatus === 1
                    ? "mdi-toggle-switch"
                    : "mdi-toggle-switch-off"
                }}
              </v-icon>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Sección: Salud SCTR (Idéntica a InsuredPanel.vue con TypeHealthRegimen = 2) -->
      <v-card flat outlined class="mb-4 section-card section-health">
        <v-card-title class="section-title py-2 px-4">
          <v-icon small class="mr-2" color="teal"
            >mdi-hospital-box-outline</v-icon
          >
          {{ editedHealthIndex > -1 ? "EDITAR" : "NUEVO" }} SEGURO DE SALUD SCTR
          <v-spacer />
          <v-btn
            v-if="editedHealthIndex > -1"
            icon
            small
            color="error"
            @click="cancelHealth"
            title="Cancelar edición"
          >
            <v-icon small>mdi-close-circle-outline</v-icon>
          </v-btn>
          <v-btn icon small color="success" @click="addHealth" title="Agregar seguro SCTR">
            <v-icon small>mdi-plus-circle-outline</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-2 pb-3">
          <v-row dense>
            <!-- Tipo de seguro (Def 1509 con valor por defecto 2 = EPS) -->
            <v-col cols="12" :sm="healthForm.TypeHealthRegimen == 2 ? 6 : 12">
              <s-select-definition
                :def="1509"
                label="Seguro de Salud SCTR"
                v-model="healthForm.TypeHealthRegimen"
                ref="TypeHealthRegimenRef"
                :add="$fun.isAdmin()"
                dense
              />
            </v-col>

            <!-- Compañía EPS / Aseguradora (Def 1529: Rimac, Pacífico, Mapfre, etc.) -->
            <v-col cols="12" sm="6" v-if="healthForm.TypeHealthRegimen == 2">
              <s-select-definition
                :def="1529"
                label="Compañía EPS / Aseguradora SCTR"
                v-model="healthForm.TypeCompany"
                ref="TypeCompanyRef"
                :add="$fun.isAdmin()"
                dense
              />
            </v-col>
          </v-row>

          <!-- Badges informativos contextuales -->
          <v-row dense v-if="healthForm.TypeHealthRegimen">
            <v-col cols="12">
              <v-alert
                v-if="healthForm.TypeHealthRegimen != 2"
                dense
                text
                type="success"
                icon="mdi-heart-pulse"
                class="mb-0 text-caption"
              >
                <strong>EsSalud:</strong> Cobertura regular de salud.
              </v-alert>
              <v-alert
                v-else
                dense
                text
                type="info"
                icon="mdi-office-building-marker-outline"
                class="mb-0 text-caption"
              >
                <strong>EPS / Seguro Privado:</strong> Seleccione la compañía aseguradora SCTR (Rimac, Pacífico, Mapfre, etc.).
              </v-alert>
            </v-col>
          </v-row>

          <!-- Lista de seguros SCTR registrados -->
          <v-data-table
            :headers="healthHeaders"
            :items="healthList"
            no-data-text="Sin seguros de salud SCTR registrados"
            class="mt-3 elevation-0 outlined-table"
            dense
            hide-default-footer
          >
            <template v-slot:[`item.TypeHealthRegimenName`]="{ item }">
              <div class="d-flex align-center">
                <v-icon small class="mr-1" color="teal">mdi-hospital-box</v-icon>
                {{ item.TypeHealthRegimenName || "EPS" }}
              </div>
            </template>

            <template v-slot:[`item.TypeCompanyName`]="{ item }">
              <span v-if="item.TypeCompanyName || item.TypeSctrHealthName" class="font-weight-medium">
                {{ item.TypeCompanyName || item.TypeSctrHealthName }}
              </span>
              <span v-else class="grey--text text-caption">—</span>
            </template>

            <!-- Chip USAR / ACTIVO (Selección única) -->
            <template v-slot:[`item.selected`]="{ item }">
              <v-chip
                small
                style="cursor: pointer"
                :color="item.selected ? 'teal darken-2' : 'grey lighten-2'"
                :text-color="item.selected ? 'white' : 'grey darken-2'"
                :disabled="item.SecStatus === 0"
                @click="selectHealth(item)"
              >
                <v-icon x-small left>{{
                  item.selected ? "mdi-check-circle" : "mdi-radiobox-blank"
                }}</v-icon>
                {{ item.selected ? "ACTIVO" : "USAR" }}
              </v-chip>
            </template>

            <template v-slot:[`item.SecStatus`]="{ item }">
              <v-icon small :color="item.SecStatus === 1 ? 'success' : 'grey'">
                {{ item.SecStatus === 1 ? "mdi-circle" : "mdi-circle-outline" }}
              </v-icon>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                small
                class="mr-1"
                color="primary"
                @click="editHealth(item)"
                title="Editar"
                >mdi-pencil-outline</v-icon
              >
              <v-icon
                small
                :color="item.SecStatus === 1 ? 'error' : 'success'"
                @click="toggleHealth(item)"
                :title="item.SecStatus === 1 ? 'Desactivar' : 'Activar'"
              >
                {{
                  item.SecStatus === 1
                    ? "mdi-toggle-switch"
                    : "mdi-toggle-switch-off"
                }}
              </v-icon>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>
  </v-card>
</template>

<script>
export default {
  name: "ContractSctrForm",

  props: {
    agtID: { type: Number, default: 0 },
    datawork: { type: Object, default: () => ({}) },
  },

  emits: ["update:selectedScpID", "update:selectedSchID"],

  data() {
    return {
      // Fechas SCTR
      sctrBeginDate: null,
      sctrEndDate: null,

      // Pensión SCTR
      pensionForm: { TypeSctrPension: null },
      pensionList: [],
      editedPensionIndex: -1,

      // Salud SCTR (Idéntico a InsuredPanel con default TypeHealthRegimen = 2)
      healthForm: {
        TypeHealthRegimen: 2,
        TypeCompany: null,
      },
      healthList: [],
      editedHealthIndex: -1,

      pensionHeaders: [
        { text: "Régimen Pensionario", value: "TypeSctrPensionName", sortable: false },
        { text: "Usar", value: "selected", sortable: false, align: "center", width: 110 },
        { text: "Estado", value: "SecStatus", sortable: false, align: "center", width: 80 },
        { text: "Acciones", value: "actions", sortable: false, align: "center", width: 100 },
      ],
      healthHeaders: [
        { text: "Régimen", value: "TypeHealthRegimenName", sortable: false, width: 140 },
        { text: "Compañía Aseguradora / EPS", value: "TypeCompanyName", sortable: false },
        { text: "Usar", value: "selected", sortable: false, align: "center", width: 110 },
        { text: "Estado", value: "SecStatus", sortable: false, align: "center", width: 80 },
        { text: "Acciones", value: "actions", sortable: false, align: "center", width: 100 },
      ],
    };
  },

  watch: {
    datawork: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!val) return;
        this.hydrateSctr(val);
      },
    },
  },

  methods: {
    hydrateSctr(data) {
      if (data.SctrBeginDate || data.AgtScrtBeginDate) {
        this.sctrBeginDate = data.SctrBeginDate || data.AgtScrtBeginDate;
      } else {
        this.sctrBeginDate = new Date().toISOString().split("T")[0];
      }
      if (data.SctrEndDate || data.AgtScrtEndDate) {
        this.sctrEndDate = data.SctrEndDate || data.AgtScrtEndDate;
      } else {
        this.sctrEndDate = null;
      }

      // Pensión SCTR
      const sctrP = data.SctrPension || data.SctrPensionCE || [];
      if (Array.isArray(sctrP) && sctrP.length > 0) {
        const targetScpID = data.ScpID || null;
        let hasSelected = false;
        this.pensionList = sctrP.map((p) => {
          const isSel = targetScpID
            ? p.ScpID === targetScpID
            : p.ScpIsDefault === 1 || p.selected === true;
          if (isSel && !hasSelected && p.SecStatus === 1) {
            hasSelected = true;
            return { ...p, selected: true };
          }
          return { ...p, selected: false };
        });

        // Si ninguno quedó seleccionado, activar el primero activo
        if (!hasSelected) {
          const firstActive = this.pensionList.find((p) => p.SecStatus === 1);
          if (firstActive) firstActive.selected = true;
        }
      } else {
        this.pensionList = [];
      }

      // Salud SCTR
      const sctrH = data.SctrHealth || data.SctrHealthCE || [];
      if (Array.isArray(sctrH) && sctrH.length > 0) {
        const targetSchID = data.SchID || null;
        let hasSelected = false;
        this.healthList = sctrH.map((h) => {
          const isSel = targetSchID
            ? h.SchID === targetSchID
            : h.SchIsDefault === 1 || h.selected === true;
          if (isSel && !hasSelected && h.SecStatus === 1) {
            hasSelected = true;
            return {
              ...h,
              TypeHealthRegimen: h.TypeHealthRegimen || 2,
              TypeCompany: h.TypeCompany || h.TypeSctrHealth,
              selected: true,
            };
          }
          return {
            ...h,
            TypeHealthRegimen: h.TypeHealthRegimen || 2,
            TypeCompany: h.TypeCompany || h.TypeSctrHealth,
            selected: false,
          };
        });

        // Si ninguno quedó seleccionado, activar el primero activo
        if (!hasSelected) {
          const firstActive = this.healthList.find((h) => h.SecStatus === 1);
          if (firstActive) firstActive.selected = true;
        }
      } else {
        this.healthList = [];
      }
    },

    // ── PENSIÓN SCTR ──────────────────────────────────────────
    addPension() {
      if (!this.pensionForm.TypeSctrPension) {
        this.$fun.alert("Seleccione el Régimen Pensionario SCTR.", "warning");
        return;
      }
      const defItem = this.$refs.sctrPensionRef?.items?.find(
        (x) => x.DedValue === this.pensionForm.TypeSctrPension,
      );
      const typeName = defItem ? defItem.DedDescription : "";

      if (this.editedPensionIndex > -1) {
        Object.assign(this.pensionList[this.editedPensionIndex], {
          ...this.pensionForm,
          TypeSctrPensionName: typeName,
          SecStatus: 1,
        });
        this.editedPensionIndex = -1;
      } else {
        const isFirst = this.pensionList.filter((p) => p.SecStatus === 1).length === 0;
        if (isFirst) {
          this.pensionList.forEach((p) => (p.selected = false));
        }
        this.pensionList.push({
          ...this.pensionForm,
          TypeSctrPensionName: typeName,
          ScpID: 0,
          AgtID: this.agtID,
          ScpIsDefault: isFirst ? 1 : 0,
          SecStatus: 1,
          selected: isFirst,
        });
      }
      this.pensionForm = { TypeSctrPension: null };
    },

    editPension(item) {
      this.editedPensionIndex = this.pensionList.indexOf(item);
      this.pensionForm = {
        TypeSctrPension: item.TypeSctrPension,
      };
    },

    cancelPension() {
      this.editedPensionIndex = -1;
      this.pensionForm = { TypeSctrPension: null };
    },

    selectPension(item) {
      if (item.SecStatus === 0) return;
      this.pensionList.forEach((p) => {
        p.selected = p === item;
        p.ScpIsDefault = p === item ? 1 : 0;
      });
      this.$emit("update:selectedScpID", item.ScpID || null);
    },

    togglePension(item) {
      item.SecStatus = item.SecStatus === 1 ? 0 : 1;
      if (item.SecStatus === 0 && item.selected) {
        item.selected = false;
        item.ScpIsDefault = 0;
        const nextActive = this.pensionList.find((p) => p.SecStatus === 1);
        if (nextActive) {
          nextActive.selected = true;
          nextActive.ScpIsDefault = 1;
          this.$emit("update:selectedScpID", nextActive.ScpID || null);
        } else {
          this.$emit("update:selectedScpID", null);
        }
      }
    },

    // ── SALUD SCTR ────────────────────────────────────────────
    addHealth() {
      if (!this.healthForm.TypeHealthRegimen) {
        this.$fun.alert("Seleccione el Régimen de Salud SCTR.", "warning");
        return;
      }
      if (this.healthForm.TypeHealthRegimen == 2 && !this.healthForm.TypeCompany) {
        this.$fun.alert("Seleccione la Compañía Aseguradora / EPS SCTR.", "warning");
        return;
      }

      const regItem = this.$refs.TypeHealthRegimenRef?.items?.find(
        (x) => x.DedValue === this.healthForm.TypeHealthRegimen,
      );
      const regName = regItem ? regItem.DedDescription : "EPS";

      const cmpItem = this.$refs.TypeCompanyRef?.items?.find(
        (x) => x.DedValue === this.healthForm.TypeCompany,
      );
      const cmpName = cmpItem ? cmpItem.DedDescription : "";

      if (this.editedHealthIndex > -1) {
        Object.assign(this.healthList[this.editedHealthIndex], {
          TypeHealthRegimen: this.healthForm.TypeHealthRegimen,
          TypeHealthRegimenName: regName,
          TypeCompany: this.healthForm.TypeHealthRegimen == 2 ? this.healthForm.TypeCompany : null,
          TypeCompanyName: this.healthForm.TypeHealthRegimen == 2 ? cmpName : "",
          TypeSctrHealth: this.healthForm.TypeCompany, // Backward compatibility
          TypeSctrHealthName: cmpName,
          SecStatus: 1,
        });
        this.editedHealthIndex = -1;
      } else {
        const isFirst = this.healthList.filter((h) => h.SecStatus === 1).length === 0;
        if (isFirst) {
          this.healthList.forEach((h) => (h.selected = false));
        }
        this.healthList.push({
          SchID: 0,
          AgtID: this.agtID,
          TypeHealthRegimen: this.healthForm.TypeHealthRegimen,
          TypeHealthRegimenName: regName,
          TypeCompany: this.healthForm.TypeHealthRegimen == 2 ? this.healthForm.TypeCompany : null,
          TypeCompanyName: this.healthForm.TypeHealthRegimen == 2 ? cmpName : "",
          TypeSctrHealth: this.healthForm.TypeCompany,
          TypeSctrHealthName: cmpName,
          SchIsDefault: isFirst ? 1 : 0,
          SecStatus: 1,
          selected: isFirst,
        });
      }

      this.healthForm = {
        TypeHealthRegimen: 2,
        TypeCompany: null,
      };
    },

    editHealth(item) {
      this.editedHealthIndex = this.healthList.indexOf(item);
      this.healthForm = {
        TypeHealthRegimen: item.TypeHealthRegimen || 2,
        TypeCompany: item.TypeCompany || item.TypeSctrHealth || null,
      };
    },

    cancelHealth() {
      this.editedHealthIndex = -1;
      this.healthForm = {
        TypeHealthRegimen: 2,
        TypeCompany: null,
      };
    },

    selectHealth(item) {
      if (item.SecStatus === 0) return;
      this.healthList.forEach((h) => {
        h.selected = h === item;
        h.SchIsDefault = h === item ? 1 : 0;
      });
      this.$emit("update:selectedSchID", item.SchID || null);
    },

    toggleHealth(item) {
      item.SecStatus = item.SecStatus === 1 ? 0 : 1;
      if (item.SecStatus === 0 && item.selected) {
        item.selected = false;
        item.SchIsDefault = 0;
        const nextActive = this.healthList.find((h) => h.SecStatus === 1);
        if (nextActive) {
          nextActive.selected = true;
          nextActive.SchIsDefault = 1;
          this.$emit("update:selectedSchID", nextActive.SchID || null);
        } else {
          this.$emit("update:selectedSchID", null);
        }
      }
    },

    validateData() {
      if (!this.sctrBeginDate) {
        this.$fun.alert("Seleccione la fecha de inicio del SCTR.", "warning");
        return false;
      }

      let selectedPension = this.pensionList.find(
        (p) => p.selected && p.SecStatus === 1,
      );
      if (!selectedPension) {
        selectedPension = this.pensionList.find((p) => p.SecStatus === 1);
        if (selectedPension) {
          selectedPension.selected = true;
          selectedPension.ScpIsDefault = 1;
        }
      }

      let selectedHealth = this.healthList.find(
        (h) => h.selected && h.SecStatus === 1,
      );
      if (!selectedHealth) {
        selectedHealth = this.healthList.find((h) => h.SecStatus === 1);
        if (selectedHealth) {
          selectedHealth.selected = true;
          selectedHealth.SchIsDefault = 1;
        }
      }

      if (!selectedPension) {
        this.$fun.alert(
          "Seleccione un Régimen Pensionario SCTR activo.",
          "warning",
        );
        return false;
      }
      if (!selectedHealth) {
        this.$fun.alert(
          "Seleccione un Seguro de Salud SCTR activo.",
          "warning",
        );
        return false;
      }
      return true;
    },

    /** Retorna los datos SCTR para el guardado. null si hay error de validación. */
    getData() {
      if (!this.validateData()) {
        return null;
      }

      let selectedPension = this.pensionList.find(
        (p) => p.selected && p.SecStatus === 1,
      );
      let selectedHealth = this.healthList.find(
        (h) => h.selected && h.SecStatus === 1,
      );

      if (!selectedPension) {
        this.$fun.alert(
          "Seleccione un Régimen Pensionario SCTR activo.",
          "warning",
        );
        return null;
      }
      if (!selectedHealth) {
        this.$fun.alert(
          "Seleccione un Seguro de Salud SCTR activo.",
          "warning",
        );
        return null;
      }

      const sctrPensionData = this.pensionList.map((p) => ({
        ...p,
        ScpID: p.ScpID > 0 ? p.ScpID : 0,
        ScpIsDefault: p === selectedPension ? 1 : 0,
        BeginDate: this.sctrBeginDate,
        EndDate: this.sctrEndDate,
      }));

      const sctrHealthData = this.healthList.map((h) => ({
        ...h,
        SchID: h.SchID > 0 ? h.SchID : 0,
        TypeHealthRegimen: h.TypeHealthRegimen || 2,
        TypeCompany: h.TypeCompany || h.TypeSctrHealth,
        TypeSctrHealth: h.TypeCompany || h.TypeSctrHealth,
        SchIsDefault: h === selectedHealth ? 1 : 0,
        BeginDate: this.sctrBeginDate,
        EndDate: this.sctrEndDate,
      }));

      return {
        SctrBeginDate: this.sctrBeginDate,
        SctrEndDate: this.sctrEndDate,
        selectedScpID: selectedPension?.ScpID || null,
        selectedSchID: selectedHealth?.SchID || null,
        selectedPensionName: selectedPension?.TypeSctrPensionName || "",
        selectedHealthRegimenName: selectedHealth?.TypeHealthRegimenName || "EPS",
        selectedCompanyName: selectedHealth?.TypeCompanyName || selectedHealth?.TypeSctrHealthName || "",
        SctrPension: sctrPensionData,
        SctrHealth: sctrHealthData,
      };
    },
  },
};
</script>

<style scoped>
.section-card {
  border-radius: 8px !important;
}
.section-title {
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.section-pension {
  border-left: 3px solid #3f51b5 !important;
}
.section-health {
  border-left: 3px solid #009688 !important;
}
.outlined-table {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}
</style>
